import React, { useState } from 'react';
import { useStore, useDispatch } from 'react-redux';
import { clearUploadData, setParsedFullAddress } from '../../redux/actions/upload';
import { toast } from '../CommonComponents/ToastComponent/toast';
import DragDropFileUpload from '../CommonComponents/DragDropFileUpload';
import { Loader } from 'rsuite';
import ExcelJS from 'exceljs';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks

const UploaderComponent = ({ onUploadData, onContinue, setOriginalFile }) => {
  const store = useStore().getState();
  const dispatch = useDispatch();
  const { upload: { uploadedData } } = store;
  const { fileName } = uploadedData;
  const [currentFileName, setCurrentFileName] = useState(fileName || '');
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isFileMatchesLimit, setIsFileMatchesLimit] = useState(true);

  const handleFileChosen = (file) => {
    setOriginalFile(file);
    if (!file || !["application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "text/csv"].includes(file.type)) {
      toast.show({
        title: 'Warning',
        description: 'File type should be .csv, .xls, or .xlsx!'
      });
      return;
    }
    setIsUploadingFile(true);
    setUploadProgress(0);

    if (file.type === 'text/csv') {
      readCsvFileInChunks(file, (parsedData, parsedColumns) => {
        if (parsedData.length > 0) {
          loadUploadedFileContent(parsedData, parsedColumns, file.name);
        }
        setIsUploadingFile(false);
      });
    } else {
      readExcelFileInChunks(file, (parsedData, parsedColumns) => {
        if (parsedData.length > 0) {
          loadUploadedFileContent(parsedData, parsedColumns, file.name);
        }
        setIsUploadingFile(false);
      });
    }

    dispatch(setParsedFullAddress(false));
  };

  const readExcelFileInChunks = async (file, callback) => {
    const reader = new FileReader();
    const workbook = new ExcelJS.Workbook();
    let rows = [];
    let columns = [];

    reader.onload = async (e) => {
      const buffer = e.target.result;
      await workbook.xlsx.load(buffer);
      const worksheet = workbook.worksheets[0];
      worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber === 1) {
          columns = row.values.slice(1);
        } else {
          rows.push(row.values.slice(1));
        }
      });

      callback(rows, columns);
    };

    reader.readAsArrayBuffer(file);
  };

  const parseCsvRow = (row) => {
    const result = [];
    let insideQuotes = false;
    let currentField = '';
    for (let i = 0; i < row.length; i++) {
      const char = row[i];
      if (char === '"') {
        insideQuotes = !insideQuotes;
      } else if (char === ',' && !insideQuotes) {
        result.push(currentField.trim());
        currentField = '';
      } else {
        currentField += char;
      }
    }
    result.push(currentField.trim());
    return result;
  };

  const readCsvFileInChunks = (file, callback) => {
    const reader = new FileReader();
    let offset = 0;
    let rows = [];
    let columns = [];
    let remaining = '';

    reader.onload = (e) => {
      const text = remaining + e.target.result;
      const newRows = text.split('\n');

      // Store incomplete line for the next chunk
      if (!text.endsWith('\n')) {
        remaining = newRows.pop();
      } else {
        remaining = '';
      }

      if (offset === 0) {
        columns = parseCsvRow(newRows.shift());
      }

      newRows.forEach(row => {
        if (row.trim()) {
          rows.push(parseCsvRow(row));
        }
      });

      offset += CHUNK_SIZE;
      setUploadProgress(Math.min(100, (offset / file.size) * 100));
      if (offset < file.size) {
        readNextChunk();
      } else {
        callback(rows, columns);
      }
    };

    const readNextChunk = () => {
      const chunk = file.slice(offset, offset + CHUNK_SIZE);
      reader.readAsText(chunk);
    };

    readNextChunk();
  };

  const loadUploadedFileContent = (data, cols, fileName) => {
    if (data.length === 0) {
      setIsUploadingFile(false);
      return;
    }
    const count = {};
    const duplicatedItems = [];
    const reindexedCols = [...cols];
    const filteredCols = reindexedCols.filter(item => item !== undefined);

    filteredCols.forEach(col => {
      if (count[col]) {
        count[col] += 1;
        return;
      }
      count[col] = 1;
    });

    for (let prop in count) {
      if (count[prop] >= 2) {
        duplicatedItems.push(prop);
      }
    }

    if (duplicatedItems.length > 0) {
      toast.show({
        title: 'Warning',
        description: `Column${duplicatedItems.length > 1 ? 's' : ''}: ${duplicatedItems.join(', ')} ${duplicatedItems.length > 1 ? 'are' : 'is'} duplicated! Please use unique column name.`
      });
      setIsUploadingFile(false);
      return;
    }

    const tableData = [];
    data.forEach(row => {
      const rowData = {};
      filteredCols.forEach((col, index) => {
        rowData[col] = row[index];
      });
      tableData.push(rowData);
    });

    setCurrentFileName(fileName);
    onUploadData(filteredCols, tableData, fileName, tableData.length);
    setIsUploadingFile(false);
  };

  const handleClickClear = () => {
    dispatch(clearUploadData());
    setCurrentFileName('');
    setUploadProgress(0);
  };

  return (
    <>
      <div className="upload-tab gap-5 d-flex align-items-center flex-column flex-md-row">
        <div className={`upload-action d-flex flex-column position-relative ${isUploadingFile && 'loading'}`}>
          {isUploadingFile && <Loader className='text-red' center vertical size="sm" />}
          <DragDropFileUpload
            currentFileName={currentFileName}
            onFileChosen={handleFileChosen}
            customClassName='upload-vendor-file'
          />
          {fileName ? (
            <button type="button" className="btn btn-red clear text-white" onClick={handleClickClear}>Remove</button>
          ) : (
            <span className='h4'>.csv, .xls, and .xlsx files accepted</span>
          )}
          {isUploadingFile && <progress value={uploadProgress} max="100"></progress>}
        </div>
        <div className="upload-note h4">
          You can upload any .csv, .xls, .xlsx file with any set of columns as long as it has 1 record per row. The next step will allow you to match your spreadsheet to the right datapoint. You'll be able to clean up or remove any corrupted data before finalizing your report.
        </div>
      </div>
      <div className="upload-tab-action pt-3 d-flex justify-content-end">
        {fileName && (
          <button type="button" className="btn btn-black continue" onClick={() => onContinue('match')} disabled={!isFileMatchesLimit}>
            Continue
          </button>
        )}
      </div>
    </>
  );
};

export default UploaderComponent;